import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from "framer-motion";
import { AuroraBackground } from "@/components/ui/aurora-background";
import {
  Search, Map, FileText, Code2, Star, Shield, BarChart2,
  Check, ChevronRight
} from 'lucide-react';
import { RainbowButton } from "@/components/ui/rainbow-button";
import FormModal from "@/components/ui/form-modal";
import Navbar from '@/components/Layout/Navbar';
import Footer from '@/components/Layout/Footer';

const geoServices = [
  {
    icon: <Search className="h-8 w-8 text-primary-300" />,
    title: "AI Visibility Audit",
    tagline: "Find out if AI knows your business exists.",
    description: "We test whether your business appears when people ask ChatGPT, Gemini, Claude, Perplexity and other AI search tools — and show you exactly where you stand against competitors.",
    includes: [
      "Brand mention testing across AI engines",
      "Competitor comparison analysis",
      "Citation source audit",
      "Prompt testing across dozens of queries",
      "Visibility score report",
    ],
  },
  {
    icon: <Map className="h-8 w-8 text-primary-300" />,
    title: "GEO Strategy & Roadmap",
    tagline: "A clear plan to get found — in AI answers.",
    description: "We create a prioritised roadmap showing exactly what needs to change for your business to appear more often in AI-generated answers across every major platform.",
    includes: [
      "Priority action list by impact",
      "Content gap identification",
      "Competitor opportunity mapping",
      "Technical fix recommendations",
      "Month-by-month execution plan",
    ],
  },
  {
    icon: <FileText className="h-8 w-8 text-primary-300" />,
    title: "AI-Optimised Content Creation",
    tagline: "Content written for AI to read, summarise and recommend.",
    description: "We create content structured in a way that AI engines can easily parse, cite, and surface in responses — going beyond traditional SEO to target generative search.",
    includes: [
      "Service pages optimised for AI citation",
      "FAQ content targeting AI Q&A queries",
      "Comparison pages for competitive positioning",
      "Expert guides and authority content",
      "Case studies and evidence-based assets",
    ],
  },
  {
    icon: <Code2 className="h-8 w-8 text-primary-300" />,
    title: "Technical GEO & Schema",
    tagline: "Make your website readable for AI systems.",
    description: "We implement the technical foundations that help AI engines and search platforms understand your business, your services, and your credibility.",
    includes: [
      "Organisation schema markup",
      "Local business schema",
      "Service schema implementation",
      "FAQ schema for key pages",
      "Structured page layouts for AI parsing",
    ],
  },
  {
    icon: <Star className="h-8 w-8 text-primary-300" />,
    title: "Authority & Citation Building",
    tagline: "Build the trust signals AI relies on.",
    description: "AI engines draw from trusted sources across the web. We build the citations, listings, and mentions that signal authority — so AI recommends your business with confidence.",
    includes: [
      "Business directory listings",
      "Industry-specific citations",
      "PR mentions and editorial coverage",
      "Review platform optimisation",
      "Professional association profiles",
    ],
  },
  {
    icon: <Shield className="h-8 w-8 text-primary-300" />,
    title: "AI Reputation Management",
    tagline: "Control how AI describes your business.",
    description: "What AI says about your business is shaped by the signals it finds online. We clean up weak or inaccurate information and build strong, consistent signals that put your best foot forward.",
    includes: [
      "Brand consistency audit",
      "Source clean-up and correction",
      "Positive authority signal building",
      "Misinformation tracking and removal",
      "Review and profile improvement",
    ],
  },
  {
    icon: <BarChart2 className="h-8 w-8 text-primary-300" />,
    title: "GEO Reporting",
    tagline: "Track your AI visibility over time.",
    description: "We monitor your progress across AI engines and deliver clear, actionable reports so you always know where you stand and what's working.",
    includes: [
      "Monthly AI visibility score",
      "Brand mention tracking",
      "Competitor comparison updates",
      "Citation growth monitoring",
      "Content performance analysis",
    ],
  },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Generative Engine Optimisation (GEO)",
  "description": "GEO services that help businesses appear in AI-generated answers across ChatGPT, Gemini, Claude, Perplexity and other AI search platforms.",
  "provider": {
    "@type": "Organization",
    "name": "Njord AI",
    "url": "https://www.njordai.co.uk/",
    "telephone": "+447737031147",
    "email": "info@njordai.co.uk"
  },
  "serviceType": "Generative Engine Optimisation",
  "areaServed": { "@type": "Country", "name": "United Kingdom" },
};

const Geo = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  return (
    <>
      <Helmet>
        <title>GEO Services | Njord - Get Found in AI Search Results</title>
        <meta name="description" content="Generative Engine Optimisation (GEO) services that get your business appearing in ChatGPT, Gemini, Claude, and Perplexity. AI visibility audits, content, schema, citations and reporting." />
        <meta name="keywords" content="GEO, generative engine optimisation, AI search visibility, ChatGPT SEO, AI citation building, AI reputation management, AI visibility audit" />
        <meta property="og:title" content="GEO Services | Njord - Get Found in AI Search Results" />
        <meta property="og:description" content="Generative Engine Optimisation services. Get your business appearing in AI-generated answers across ChatGPT, Gemini, Claude and Perplexity." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.njordai.co.uk/geo" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://www.njordai.co.uk/geo" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-b from-white to-black">
        <AuroraBackground className="dark h-[80vh]">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: "easeInOut" }}
            className="relative flex flex-col gap-4 items-center justify-center px-4 container mx-auto text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white max-w-4xl leading-tight">
              Get Found Where Answers Are Now Generated
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mt-2">
              When your customers ask ChatGPT, Gemini, or Perplexity about your industry — does your business come up? We make sure it does.
            </p>
            <div className="mt-6">
              <RainbowButton onClick={() => setIsModalOpen(true)}>
                Get Your GEO Audit
              </RainbowButton>
            </div>
          </motion.div>
        </AuroraBackground>
      </div>

      {/* What is GEO */}
      <section className="bg-black py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              What is GEO?
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed">
              Search has changed. People no longer just click links — they ask AI assistants and get direct answers. <strong className="text-white">Generative Engine Optimisation (GEO)</strong> is the practice of making sure your business is the one those AI systems cite, recommend, and describe when your ideal customers ask questions in your industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { platform: "ChatGPT", desc: "The world's most used AI assistant, answering millions of business queries daily." },
              { platform: "Google Gemini", desc: "Google's AI powering AI Overviews that now appear above organic results." },
              { platform: "Perplexity", desc: "The fastest-growing AI search engine with a highly engaged, research-driven audience." },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 text-center"
              >
                <h3 className="text-white font-semibold text-lg mb-2">{item.platform}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-black py-24 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our GEO Services
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              A complete suite of services to build, improve and track your presence in AI-generated answers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {geoServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index % 2) * 0.1, duration: 0.5 }}
                className="group bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-white/20 rounded-2xl p-8 transition-all duration-300"
              >
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                    {service.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold text-white mb-1">{service.title}</h3>
                    <p className="text-primary-300 text-sm font-medium mb-3">{service.tagline}</p>
                    <p className="text-gray-400 text-sm leading-relaxed mb-5">{service.description}</p>
                    <ul className="space-y-2">
                      {service.includes.map((item, i) => (
                        <li key={i} className="flex items-center gap-2.5 text-sm text-gray-300">
                          <Check className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why GEO matters */}
      <section className="bg-black py-24 border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Why Your Business Needs GEO Now
                </h2>
                <p className="text-gray-300 leading-relaxed mb-6">
                  AI search is already shaping buying decisions. Businesses that invest early in GEO will own the AI-generated answers in their industry — while competitors remain invisible.
                </p>
                <ul className="space-y-4">
                  {[
                    "AI answers are replacing traditional search results",
                    "Early movers gain a lasting citation advantage",
                    "AI-cited businesses see measurable increases in trust and enquiries",
                    "Most businesses have zero GEO strategy today",
                  ].map((point, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-300">
                      <ChevronRight className="h-5 w-5 text-primary-300 flex-shrink-0 mt-0.5" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { stat: "40%", label: "of searches now receive AI-generated answers" },
                  { stat: "3x", label: "higher trust when a business is cited by AI" },
                  { stat: "68%", label: "of users act on the first AI answer they receive" },
                  { stat: "~0%", label: "of your competitors have a GEO strategy" },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white/5 border border-white/10 rounded-xl p-5 text-center"
                  >
                    <div className="text-3xl font-bold text-white mb-1">{item.stat}</div>
                    <div className="text-gray-400 text-xs leading-snug">{item.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-24 border-t border-white/5">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Find Out Where You Stand in AI Search
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Start with a GEO Audit — we'll test your business across every major AI platform and show you exactly what needs fixing.
            </p>
            <RainbowButton onClick={() => setIsModalOpen(true)}>
              Book Your Free Discovery Call
            </RainbowButton>
          </motion.div>
        </div>
      </section>

      <FormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <Footer />
    </>
  );
};

export default Geo;
